<template>
    <div class="flex flex-wrap">
        <div class="mt-2 flex flex-col w-full">
            <div class="w-full shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                <table class="table-default table-planning" v-if="levels && levels.length > 0">
                    <thead>
                        <tr>
                            <th scope="col" class="hidden xl:table-cell">
                                {{ $t('shared.pyramid_auction.levels.table_columns.level') }}
                            </th>
                            <th scope="col" class="hidden xl:table-cell">
                                {{ $t('shared.pyramid_auction.levels.table_columns.price_drop') }}
                            </th>
                            <th scope="col" class="hidden xl:table-cell">
                                {{ $t('shared.pyramid_auction.levels.table_columns.fixed_time') }}
                            </th>
                            <th scope="col">{{ $t('shared.pyramid_auction.levels.table_columns.price') }}</th>
                            <th scope="col" class="hidden md:table-cell">
                                {{ $t('shared.pyramid_auction.levels.table_columns.qty_planned') }}
                            </th>
                            <th scope="col">{{ $t('shared.pyramid_auction.levels.table_columns.qty_sold') }}</th>
                            <th scope="col">{{ $t('shared.pyramid_auction.levels.table_columns.status') }}</th>
                            <th scope="col">
                                {{ $t('shared.pyramid_auction.levels.table_columns.floor_sales' + this.$page.props.tenant.labels_key) }}
                            </th>
                            <th scope="col">
                                {{ $t('shared.pyramid_auction.levels.table_columns.merch_sales' + this.$page.props.tenant.labels_key) }}
                            </th>
                            <th scope="col" class="hidden lg:table-cell" v-if="isRunning">
                                {{ $t('shared.pyramid_auction.levels.table_columns.adjust') }}
                            </th>
                        </tr>
                    </thead>
                    <tbody class="bg-white">
                        <template v-for="(level, index) in levels">
                            <tr v-if="canAddPriceDropPhase(index) && isRunning && startsLater(level)">
                                <td colspan="6" class="hidden lg:table-cell text-right border-bottom-1"></td>
                                <td colspan="3" class="hidden xl:table-cell text-right border-bottom-1"></td>
                                <td class="hidden lg:table-cell text-right border-bottom-1">
                                    <button
                                        class="btn btn-primary"
                                        @click="addPriceDropPhaseBeforeLevel(level)">+
                                    </button>
                                </td>
                            </tr>
                            <tr
                                :key="level.id"
                                :class="{
                                        'bg-gray-50': level.phase % 2 === 1,
                                        'price-drop': level.price_drop === 1 || index === 0
                                    }"
                            >
                                <td class="hidden xl:table-cell py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                                    {{ level.sequence }}
                                </td>
                                <td class="hidden xl:table-cell">
                                    <icon v-if="level.price_drop === 1" name="badge-check" class="h-6 w-6"/>
                                </td>
                                <td class="hidden xl:table-cell">{{
                                        level.date_start | formatTime($t('locale.formatTimeTwelveHours'), tz.auctionTimeZone(), tz.code())
                                    }}
                                </td>
                                <td>{{ level.price | toCurrency }}</td>
                                <td class="hidden md:table-cell">{{ level.quantity }}</td>
                                <td>{{ level.sold > 0 ? level.sold : '' }}</td>
                                <td class="px-3 py-4 text-sm text-gray-500 text-center">
                                    <auction-state :state="level.state"></auction-state>
                                </td>
                                <td>{{ level.floor_sales | toCurrency }}</td>
                                <td>{{ level.merch_sales | toCurrency }}</td>
                                <td
                                    class="py-4 text-sm text-right border-bottom-0 hidden lg:table-cell"
                                    v-if="isRunning">
                                    <button
                                        class="btn btn-primary"
                                        @click="adjustParameters(level)"
                                        v-if="level.price_drop === 1 && level.state === 'future' && startsLater(level)">
                                        {{ $t('shared.edit') }}
                                    </button>
                                    <button
                                        class="btn btn-danger border-none"
                                        @click="deletePhase(level)"
                                        v-if="level.price_drop === 1 && level.state === 'future' && startsLater(level)">
                                        {{ $t('shared.delete') }}
                                    </button>
                                </td>
                            </tr>
                        </template>
                        <tr v-if="canAddPriceDropPhase(levels.length) && isRunning">
                            <td colspan="6" class="hidden lg:table-cell text-right border-bottom-1"></td>
                            <td colspan="3" class="hidden xl:table-cell text-right border-bottom-1"></td>
                            <td class="hidden lg:table-cell text-right border-bottom-1">
                                <button
                                    class="btn btn-primary"
                                    @click="addPriceDropPhaseBeforeLevel(null)">+
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
                <div v-if="!levels || levels.length < 1" class="m-3">{{ $t('shared.pyramid_auction.no_levels') }}</div>
            </div>
        </div>

        <modal
            ref="deletePhase"
            @cancel="deletePhaseClosed()"
            @confirm="deletePhaseConfirmed()"
            :show-cancel="true"
            :show-confirm="true"
            :title="$t('shared.pyramid_auction.delete_phase.title')"
        >
            <p class="">{{ $t('shared.pyramid_auction.delete_phase.message') }}</p>
            <div class="loading-indicator" v-if="sendingDeletePhase">
                <icon name="clock" class="w-8 h-8 mx-auto animate-spin"/>
            </div>
            <alert-message
                v-if="deletePhaseErrors && deletePhaseErrors.length > 0"
                :errorType="ERROR_TYPE_ERROR"
                :show-as-list="false"
                :messages="deletePhaseErrors"
                :display-errors-title="false"
                @close="deletePhaseFailedAlertClosed()"
                class="sm:px-0 lg:px-0 pr-4 sm:pr-6 lg:pr-4"
            />
        </modal>
    </div>
</template>

<script>

import { Link } from '@inertiajs/vue2';
import Icon from '@/Shared/Icon';
import Modal from "@/Shared/Modals/Modal";
import PageHeadline from "@/Shared/PageHeadline";
import AuctionState from "../Auction/AuctionState";
import TimeZoneService from "../../Services/TimeZoneService";
import AdjustMaxPriceModal from "../Modals/AdjustMaxPriceModal.vue";
import {ErrorTypes} from "../../mixins/ErrorTypes";
import AlertMessage from "../Alerts/AlertMessage.vue";
import moment from "moment";

export default {
    components: {
        Link,
        Icon,
        PageHeadline,
        AlertMessage,
        Modal,
        AuctionState,
        AdjustMaxPriceModal
    },
    mixins: [
        ErrorTypes
    ],
    props: {
        isRunning: Boolean,
        levels: Array,
        showComparison: {
            type: Boolean,
            default: false
        },
        timeBetweenPriceDropsAndShows: {
            type: Number,
            default: 120
        },
        auction: Object,
    },
    computed: {
        tz() {
            return TimeZoneService;
        },
    },
    data() {
        return {
            levelToDelete: null,
            sendingDeletePhase: false,
            deletePhaseErrors: [],
        }
    },
    methods: {
        rowSpanAction(level) {
            return this.levels.filter((lvl) => lvl.phase === level.phase).length;
        },
        adjustParameters(level) {
            this.$emit("adjustPriceDrop", level);
        },
        canAddPriceDropPhase(index) {
            if (this.levels.length <= index) { // insert after last item should be possible
                return true;
            }
            if (this.levels[index] && this.levels[index]['price_drop'] === 1 && this.levels[index]['state'] === 'future') {
                return true;
            }
            return false;
        },
        addPriceDropPhaseBeforeLevel(level) {
            // Show modal dialog to add a price drop phase
            this.$emit("addPriceDropPhaseBeforeLevel", level);
        },
        deletePhase(level) {
            this.deletePhaseErrors = [];
            this.levelToDelete = level;
            this.$refs.deletePhase.show();
        },
        deletePhaseFailedAlertClosed() {
            this.deletePhaseErrors = [];
        },
        deletePhaseClosed() {
            this.levelToDelete = null;
            this.$refs.deletePhase.hide();
        },
        deletePhaseConfirmed() {
            if (this.levelToDelete === null) {
                this.sendingDeletePhase = false;
                this.$refs.deletePhase.hide();
                return;
            }
            this.sendingDeletePhase = true;
            axios.delete(
                this.route('auctions.deletePhase', {
                    auction: this.$page.props.auction.id,
                    phase: this.levelToDelete.phase
                })).then(() => {
                    this.levelToDelete = null;
                    this.$refs.deletePhase.hide();
                    this.$emit('auction-modified');
                }).catch((err) => {
                    this.deletePhaseErrors = err;
                    console.error(err);
                }).finally(() => {
                    this.sendingDeletePhase = false;
                });
        },
        startsLater(level) {
            let now = moment().add(this.timeBetweenPriceDropsAndShows, 's');
            let levelStart = moment.tz(level.date_start, this.tz.auctionTimeZone());
            let campaign = this.getCampaignByTime(levelStart);
            if (campaign && campaign.date_end) {
                let dates = [levelStart];
                dates.push(moment.tz(campaign.date_start, this.tz.auctionTimeZone()));
                return moment.min(dates.filter(d => d._isValid)).isAfter(now);
            }
            return levelStart.isAfter(now);
        },
        getCampaignByTime(priceDropTime) {
            for (let i = 0; i < this.auction.campaigns.length; i++) {
                let start = moment.tz(this.auction.campaigns[i].date_start, this.tz.auctionTimeZone());
                let end = moment.tz(this.auction.campaigns[i].date_end, this.tz.auctionTimeZone());
                if (priceDropTime < end && priceDropTime > start) {
                    return this.auction.campaigns[i];
                }
            }
            return null;
        }
    }
}
</script>
